import { TermsOfUse } from '@copilot/data/responses/interface';
import { useFetch } from '@copilot/common/hooks/common';
import { PermissionActions } from '@copilot/common/store/actions/permissions';
import { FullPermission, validateIsVisible } from '@copilot/common/hooks/permission';
import { useSelector } from 'react-redux';
import { PermissionSelectors } from '@copilot/common/store/selectors/permission';
import { useState, useEffect } from 'react';
import { AIFeatureManager } from '@copilot/data';

/**
 * Hook for managing AI Data Usage Terms of Use acknowledgment.
 *
 * @returns Object with hasAcknowledged, acknowledgeTermsOfUse, and isLoading.
 */
export function useAIDataUsageTermsOfUse() {
    const permissions = useSelector(PermissionSelectors.getPermissions);
    const hasAcknowledged = validateIsVisible(permissions[TermsOfUse.AIDataUsage]);

    const [{ isFetching }, acknowledgeTermsOfUseRaw] = useFetch(
        () => AIFeatureManager.acknowledgeAIDataUsageTermsOfUse(),
        PermissionActions.setPermissions,
        () => ({ [TermsOfUse.AIDataUsage]: FullPermission })
    );
    /**
     * Triggers the acknowledgment of AI Data Usage Terms of Use.
     */
    function acknowledgeTermsOfUse() {
        return acknowledgeTermsOfUseRaw();
    }
    return { hasAcknowledged, acknowledgeTermsOfUse, isLoading: isFetching };
}

/**
 * Hook for OrgAdmin acknowledging AI Data Usage Terms on behalf of team members.
 * This hook does NOT update the current user's permission store.
 *
 * @param orgMemberId - Organization member ID to manage terms for.
 * @returns Object with hasAcknowledged, acknowledgeTermsOfUse, and isLoading.
 */
export function useTeamMemberAIDataUsageTermsOfUse(orgMemberId: string) {
    const [{ isFetching: isAcknowledgingFetching }, acknowledgeTermsOfUseRaw] = useFetch(() =>
        AIFeatureManager.acknowledgeAIDataUsageTermsOfUse(orgMemberId)
    );

    const [hasAcknowledged, setHasAcknowledged] = useState<boolean>(false);
    const [isCheckingFetching, setIsCheckingFetching] = useState<boolean>(false);

    // Fetch acknowledgment status only on mount and orgMemberId change
    useEffect(() => {
        async function checkTermsStatus() {
            setIsCheckingFetching(true);
            try {
                const result =
                    await AIFeatureManager.hasAcknowledgedAIDataUsageTermsOfUse(orgMemberId);
                setHasAcknowledged(result || false);
            } catch (error) {
                setHasAcknowledged(false);
            } finally {
                setIsCheckingFetching(false);
            }
        }
        checkTermsStatus();
    }, [orgMemberId]);

    /**
     * Triggers the acknowledgment of AI Data Usage Terms for the specified team member.
     */
    async function acknowledgeTermsOfUse() {
        await acknowledgeTermsOfUseRaw();
        setHasAcknowledged(true);
    }

    return {
        hasAcknowledged,
        acknowledgeTermsOfUse,
        isLoading: isCheckingFetching || isAcknowledgingFetching,
    };
}
